// ops / api — the Ops pages' reads and actions: fetch, then parse the wire
// value with the shared contract (`@bilda/app/ops`, `@bilda/app/
// feedbackAdmin`) at the boundary. Impure (network); used only by the
// pages' hosts.

export type { AppHealth, OpsActionFailure };
export { opsApi };

import {
    type FeedbackAdminList,
    feedbackAdminListDto,
} from "@bilda/app/feedbackAdmin";
import {
    type OpsAccounts,
    type OpsEnvironment,
    opsAccountsDto,
    opsEnvironmentDto,
} from "@bilda/app/ops";
import { Result } from "@bilda/utils";
import { type ExternalError, mkMalformed } from "../externalError.js";
import { getAdmin, postAdmin } from "../httpClient.js";

type OpsRead<T> = Promise<Result.Result<T, ExternalError>>;

// What /healthz and /readyz say together: the process answers and reads
// its data; it answers but cannot read its data; or it does not answer.
type AppHealth = "ready" | "not_ready" | "down";

type Parser<T> = {
    readonly safeParse: (
        v: unknown,
    ) =>
        | { readonly success: true; readonly data: T }
        | { readonly success: false };
};

const parsed = async <T>(
    read: OpsRead<unknown>,
    schema: Parser<T>,
    what: string,
): OpsRead<T> => {
    const r = await read;
    if (Result.isErr(r)) return r;
    const p = schema.safeParse(r.value);
    return p.success
        ? Result.ok(p.data)
        : Result.err(mkMalformed(`${what} did not match`));
};

// Why an action did not happen: the server refused it by name (the wire
// error's `type`, e.g. `feedback_already_handled`), or it failed otherwise.
type OpsActionFailure =
    | { readonly type: "ops_refused"; readonly reason: string }
    | { readonly type: "ops_failed"; readonly error: ExternalError };

type OpsAction = Promise<Result.Result<void, OpsActionFailure>>;

// The refusal's name off a 4xx body (`{ ok: false, error: { type } }`).
const reasonOf = (detail: string | undefined): string | undefined => {
    if (detail === undefined) return undefined;
    try {
        const body: unknown = JSON.parse(detail);
        if (typeof body !== "object" || body === null || !("error" in body))
            return undefined;
        const error: unknown = body.error;
        return typeof error === "object" &&
            error !== null &&
            "type" in error &&
            typeof error.type === "string"
            ? error.type
            : undefined;
    } catch {
        return undefined;
    }
};

const done = async (read: OpsRead<unknown>): OpsAction => {
    const r = await read;
    if (Result.isOk(r)) return Result.ok(undefined);
    const reason =
        r.error._tag === "External.Rejected"
            ? reasonOf(r.error.detail)
            : undefined;
    return Result.err(
        reason === undefined
            ? { type: "ops_failed", error: r.error }
            : { type: "ops_refused", reason },
    );
};

// One probe: did it answer 2xx? Never throws.
const answers = (path: string): Promise<boolean> =>
    fetch(path, { method: "GET", cache: "no-store" }).then(
        (res) => res.ok,
        () => false,
    );

const status = (workspaceId: string, reportId: string, verb: string) =>
    `feedback/${encodeURIComponent(workspaceId)}/${encodeURIComponent(reportId)}/${verb}`;

const opsApi = {
    environment: async (): OpsRead<OpsEnvironment> => {
        const r = await parsed(
            getAdmin("ops/environment"),
            opsEnvironmentDto,
            "environment",
        );
        return Result.isErr(r) ? r : Result.ok(r.value.environment);
    },
    health: async (): Promise<AppHealth> => {
        const [alive, ready] = await Promise.all([
            answers("/healthz"),
            answers("/readyz"),
        ]);
        return !alive ? "down" : ready ? "ready" : "not_ready";
    },
    feedback: (): OpsRead<FeedbackAdminList> =>
        parsed(getAdmin("feedback"), feedbackAdminListDto, "feedback list"),
    markHandled: (workspaceId: string, reportId: string): OpsAction =>
        done(postAdmin(status(workspaceId, reportId, "handled"))),
    reopen: (workspaceId: string, reportId: string): OpsAction =>
        done(postAdmin(status(workspaceId, reportId, "reopen"))),
    accounts: (): OpsRead<OpsAccounts> =>
        parsed(getAdmin("users/data"), opsAccountsDto, "accounts"),
    createAccount: (email: string): OpsAction =>
        done(postAdmin("users", { email })),
    disableAccount: (id: string): OpsAction =>
        done(postAdmin(`users/${encodeURIComponent(id)}/disable`)),
    enableAccount: (id: string): OpsAction =>
        done(postAdmin(`users/${encodeURIComponent(id)}/enable`)),
    removeAccount: (id: string, confirmEmail: string): OpsAction =>
        done(
            postAdmin(`users/${encodeURIComponent(id)}/remove`, {
                confirmEmail,
            }),
        ),
};
